// Pure helpers for the "Office 365 disconnected — reconnect" banner.
//
// Split out of the component (same pattern as connect-email-banner-helpers)
// so the show/hide decision is unit-testable in the Node pool with no JSX
// and no browser globals. The banner itself wires these into React state.
//
// The distinction this file encodes is the whole point of the banner:
//   * "never connected" / "revoked" (deliberate disconnect) → a gentle,
//     dismissible "Connect your email" nudge (ConnectEmailBanner owns it).
//   * "was connected, now BROKEN" (expired / error / subscription_lost) →
//     a prominent, NON-dismissible "reconnect" alert (this banner owns it),
//     because the user's calendar + tasks silently went empty and nothing
//     else tells them why. (Nate's 2026-07-06 report.)
//
// Kept dependency-free: `status` is typed as a plain string union mirror of
// the API's MicrosoftGraphConnectionStatus rather than imported from api.ts,
// so this module never drags api.ts's browser globals into the test program.

// The connection states that mean "this was working and is now broken" —
// i.e. the user HAD an active connection and it lapsed. These are the only
// states the reconnect banner surfaces. `revoked` is deliberately excluded:
// it's the result of the user clicking Disconnect, so it's the gentle-nudge
// case, not a surprise breakage.
export const BROKEN_GRAPH_STATUSES = [
  "expired",
  "error",
  "subscription_lost",
] as const;

export type BrokenGraphStatus = (typeof BROKEN_GRAPH_STATUSES)[number];

/** True when `status` is one of the "was connected, now broken" states. */
export function isBrokenGraphStatus(
  status: string | null | undefined,
): status is BrokenGraphStatus {
  return (
    status != null &&
    (BROKEN_GRAPH_STATUSES as readonly string[]).includes(status)
  );
}

export interface ReconnectBannerState {
  /** Status fetch has finished (success OR failure). */
  loaded: boolean;
  /**
   * The connection's status column: "active" | "expired" | "revoked" |
   * "error" | "subscription_lost" | null. null = never connected (or the
   * status call failed). We only surface the reconnect banner on an
   * explicit broken state so a transient error or a never-connected user
   * never sees it.
   */
  status: string | null;
}

/**
 * True when the reconnect banner should show: the status fetch has
 * resolved AND the connection is in a broken-but-previously-active state.
 * Loading (`loaded=false`), never-connected (`status=null`), healthy
 * (`active`), and deliberate-disconnect (`revoked`) all hide it.
 */
export function shouldShowReconnectBanner(state: ReconnectBannerState): boolean {
  if (!state.loaded) return false;
  return isBrokenGraphStatus(state.status);
}

// One-click reconnect target — the same server 302 the Preferences page
// uses. Redirecting the browser here starts the Microsoft OAuth flow and
// returns to /preferences?graph_success=1 on success (a full page load, so
// the banner re-checks status and hides itself).
export const GRAPH_CONNECT_URL = "/api/integrations/microsoft-graph/connect";

/**
 * Short, plain-English explanation of WHY the connection is broken, keyed
 * off the status. Kept human — no error codes in the primary line.
 */
export function reconnectReasonLine(status: string | null): string {
  switch (status) {
    case "expired":
      return "Your Office 365 sign-in expired.";
    case "subscription_lost":
      return "Your Office 365 mailbox sync dropped.";
    case "error":
    default:
      return "Your Office 365 connection dropped.";
  }
}
